"use client";

import { Badge } from "@elion-health/ui";
import {
  COVERAGE_FILL_CLASS,
  COVERAGE_LABEL,
  type CoverageState,
} from "./coverage";
import { HoverPopover } from "./HoverPopover";

/** How many applications a step's panel names before collapsing the rest. */
const PANEL_APPLICATION_LIMIT = 6;

export interface StepSegmentApplication {
  id: string;
  name: string;
  vendor: string | null;
}

export interface StepSegment<S extends string = CoverageState> {
  id: string;
  /** 1-based position within the workflow. */
  position: number;
  name: string;
  definition?: string | null;
  /** The step's state; `"none"` draws as a hollow pill. */
  coverage: S;
  applications: StepSegmentApplication[];
}

/** How a strip colors and names its states. */
export interface StepPalette<S extends string> {
  fill: Record<S, string>;
  label: Record<S, string>;
}

/** The map's states: how many applications have documented support at a step. */
export const COVERAGE_PALETTE: StepPalette<CoverageState> = {
  fill: COVERAGE_FILL_CLASS,
  label: COVERAGE_LABEL,
};

export interface StepSegmentBarProps<S extends string = CoverageState> {
  steps: StepSegment<S>[];
  /** The states' colors and names; the map's by default. */
  palette?: StepPalette<S>;
  /** Accessible name for the strip, e.g. "Steps in Inventory Management". */
  label: string;
  /**
   * `fill` stretches the pills across the row. `fixed` gives every pill the
   * same width, so strips stacked with the same `slots` line up step by step.
   */
  layout?: "fill" | "fixed";
  /** With `fixed`, the step count to size the track for (the longest strip). */
  slots?: number;
  /** The step to mark as current, e.g. the one scrolled to. */
  activeStepId?: string | null;
  /** Called when a step pill is clicked, e.g. to scroll to that step. */
  onStepSelect?: (stepId: string) => void;
  /** A line at the foot of each step's panel saying what a click does. */
  selectHint?: string;
  /** When set, only these steps stay at full strength; the rest fade back. */
  highlightStepIds?: ReadonlySet<string> | null;
  /** `sm` draws thinner pills, for dense lists of strips. */
  size?: "md" | "sm";
}

/** Fixed-layout pill width and gap, in px (matching the classes below). */
const FIXED_PILL = 32;
const FIXED_GAP = 6;

/**
 * A workflow as a strip of pills, one per step in order, colored by coverage.
 * Hovering or focusing a pill shows the step and its documented applications.
 */
export function StepSegmentBar<S extends string = CoverageState>({
  steps,
  palette = COVERAGE_PALETTE as unknown as StepPalette<S>,
  label,
  layout = "fill",
  slots = steps.length,
  activeStepId,
  onStepSelect,
  selectHint,
  highlightStepIds,
  size = "md",
}: StepSegmentBarProps<S>) {
  const fixed = layout === "fixed";
  return (
    <ol
      aria-label={label}
      className={`flex items-stretch ${fixed ? "max-w-full gap-[6px]" : "gap-1"}`}
      style={
        fixed
          ? { width: slots * FIXED_PILL + (slots - 1) * FIXED_GAP }
          : undefined
      }
    >
      {steps.map((step) => (
        <li
          key={step.id}
          className={`flex transition-opacity ${fixed ? "w-[32px] min-w-1.5 shrink" : "min-w-1.5 flex-1"} ${
            highlightStepIds && !highlightStepIds.has(step.id)
              ? "opacity-15"
              : ""
          }`}
        >
          <StepPill
            step={step}
            palette={palette}
            fixed={fixed}
            active={step.id === activeStepId}
            onSelect={onStepSelect}
            selectHint={selectHint}
            small={size === "sm"}
          />
        </li>
      ))}
    </ol>
  );
}

function stepDescription<S extends string>(
  step: StepSegment<S>,
  palette: StepPalette<S>,
): string {
  const apps = step.applications.map((app) => app.name).join(", ");
  return `Step ${step.position}: ${step.name}. ${palette.label[step.coverage]}${apps ? `: ${apps}` : ""}.`;
}

function StepPill<S extends string>({
  step,
  palette,
  fixed,
  active,
  onSelect,
  selectHint,
  small,
}: {
  step: StepSegment<S>;
  palette: StepPalette<S>;
  fixed: boolean;
  active: boolean;
  onSelect?: (stepId: string) => void;
  selectHint?: string;
  small: boolean;
}) {
  const shown = step.applications.slice(0, PANEL_APPLICATION_LIMIT);
  const hidden = step.applications.length - shown.length;
  return (
    <HoverPopover
      label={stepDescription(step, palette)}
      onClick={onSelect && (() => onSelect(step.id))}
      side={fixed ? "bottom" : "top"}
      align={fixed ? "start" : "center"}
      className="w-[250px] px-3 py-2.5"
      triggerClassName={`w-full rounded-full border transition-transform hover:scale-y-125 ${
        small ? "h-3" : fixed ? "h-4" : "h-5"
      } ${
        active
          ? "relative z-10 outline-[1.5px] outline-offset-2 outline-(--fg-primary) outline-solid"
          : ""
      } ${
        step.coverage === "none"
          ? "border-(--border-strong) bg-(--bg-canvas)"
          : `border-transparent ${palette.fill[step.coverage]}`
      }`}
      trigger={null}
    >
      <p className="text-[11.5px] font-semibold text-(--fg-primary)!">
        <span className="mr-1 tabular-nums text-(--fg-muted)">
          {step.position}.
        </span>
        {step.name}
      </p>
      {step.definition && (
        <p className="mt-1! line-clamp-3 text-[10.5px] leading-snug text-(--fg-muted)!">
          {step.definition}
        </p>
      )}
      <p className="mt-1.5! text-[10.5px] font-medium text-(--fg-secondary)!">
        {palette.label[step.coverage]}
      </p>
      {shown.length > 0 && (
        <ul className="mt-1.5 flex flex-wrap gap-1">
          {shown.map((app) => (
            <li key={app.id} className="flex max-w-full">
              <Badge
                text={app.name}
                title={app.vendor ? `${app.name} · ${app.vendor}` : app.name}
                className="max-w-full truncate px-2 text-[11px]"
              />
            </li>
          ))}
          {hidden > 0 && (
            <li className="px-1 text-[10.5px] font-medium text-(--fg-muted)">
              +{hidden} more
            </li>
          )}
        </ul>
      )}
      {selectHint && (
        <p className="mt-2! text-[11px] font-medium text-(--fg-brand)!">
          {selectHint}
        </p>
      )}
    </HoverPopover>
  );
}
